// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { NotConnected1 } from '../../../../assets/img';
import { Target } from '../../../../client';
import InfosLabel, { InfosLabelTypes } from '../../../../components/InfosLabel';
import Modal from '../../../../components/Modal';
import ModalFixedContent from '../../../../components/ModalFixed';
import NotFoundView from '../../../../components/NotFoundView';
import WinaLoader from '../../../../components/WinaLoader';
import { ModalContentSpacing } from '../../../../constants/ui/modal';
import { getAppDOMElement } from '../../../../DOMElements';
import useRouter from '../../../../hooks/useRouter';
import { __ } from '../../../../i18n/l10n';
import {
  selectUserConnected,
  selectUserConnecting,
  selectUserDidReceivedUserData,
} from '../../../../selectors/user/userPrimary.selector';
import { RoutePatterns } from '../../../../types/routes';
import { showWebsiteLoginForm } from '../../../../utils';
import RoutesRedirectionFactory from '../../../../utils/routes/RoutesRedirectionFactory';
import { BoomContext } from '../../contexts/BoomContext';
import { BoomModalContext } from '../../contexts/BoomModalContext';
import {
  selectBoomGlobalOperationEndDate,
  selectBoomGlobalSkinId,
  selectBoomGlobalState,
  selectBoomGlobalStatus,
} from '../../selectors/global/globalPrimary.selector';
import { selectIsBoomEnabled } from '../../selectors/runtime.selector';
import { selectBoomUserStatus } from '../../selectors/user/userPrimary.selector';
import { BoomGlobalStatuses } from '../../types/global';
import { BoomUserStatuses } from '../../types/user';
import BoomEnrollmentContainer from '../BoomEnrollment/BoomEnrollmentContainer';
import BoomHomeContainer from '../BoomHome/BoomHomeContainer';
import BoomStepViewContainer from '../BoomStepView/BoomStepViewContainer';

const StyledWinaLoader = styled(WinaLoader)`
  margin: 0;
  margin-top: 55%;
  transform: translateY(-50%);
`;

const RouterSwitchWrapper = styled.div`
  position: relative;
`;

const StyledInfosLabel = styled(InfosLabel)`
  margin: 0;
  border-radius: 0;
  text-align: center;

  ${Target.__MOBILE__
    ? css`
        position: absolute;
        left: 0;
        width: 100%;
        top: 0;
        z-index: 1;
      `
    : undefined}
`;

const BoomMainRouterSwitch = () => {
  const history = useHistory();

  const didReceivedUserData = useSelector(selectUserDidReceivedUserData);
  const BoomUserStatus = useSelector(selectBoomUserStatus);
  const BoomUserStatusRef = useRef(BoomUserStatus);

  useLayoutEffect(() => {
    // Si l'utilisateur doit être redirigé ou non vers la vue d'enregistrement à l'opération

    if (didReceivedUserData) {
      if (!BoomUserStatusRef.current || BoomUserStatusRef.current === BoomUserStatuses.OFF) {
        history.push(RoutesRedirectionFactory.getBoomEnrollementRouteConfig());
      }
    }
  }, [didReceivedUserData, history]);

  return (
    <Switch>
      <Route path={RoutePatterns.BOOM} exact>
        <BoomHomeContainer />
      </Route>
      <Route path={RoutePatterns.BOOM_STEP} exact>
        <BoomStepViewContainer />
      </Route>
    </Switch>
  );
};

type BoomSubRouterSwitchProps = {
  className?: string;
};

const BoomSubRouterSwitch = ({ className }: BoomSubRouterSwitchProps) => {
  const history = useHistory();
  const isConnected = useSelector(selectUserConnected);
  const isConnecting = useSelector(selectUserConnecting);
  const BoomUserStatus = useSelector(selectBoomUserStatus);
  const didReceivedUserData = useSelector(selectUserDidReceivedUserData);
  const isBoomEnabled = useSelector(selectIsBoomEnabled);
  const BoomGlobalState = useSelector(selectBoomGlobalState);

  const globalStatus = useSelector(selectBoomGlobalStatus);
  const operationEndDate = useSelector(selectBoomGlobalOperationEndDate);

  const isOperationEnding = globalStatus === BoomGlobalStatuses.ENDING;
  const willOperationEndSoon = (operationEndDate ?? 0) - Date.now() < 1000 * 3600 * 24;

  const isConnectedRef = useRef(isConnected);
  const isConnectingRef = useRef(isConnecting);
  const BoomUserStatusRef = useRef(BoomUserStatus);
  const BoomGlobalStateRef = useRef(BoomGlobalState);

  const [bannerElement, setBannerElement] = useState();
  const routerSwitchWrapperRef = useRef();
  const showEndingBanner = useMemo(
    () => isOperationEnding || willOperationEndSoon,
    [isOperationEnding, willOperationEndSoon],
  );

  const bannerLabel = useMemo(() => {
    if (isOperationEnding) {
      return __('The game is over');
    }

    if (willOperationEndSoon) {
      return __('The game will end in under 24 hours');
    }
  }, [isOperationEnding, willOperationEndSoon]);

  const skinId = useSelector(selectBoomGlobalSkinId);

  const boomContextValue = useMemo(
    () => ({
      skinId,
    }),
    [skinId],
  );

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    isConnectingRef.current = isConnecting;
  }, [isConnecting]);

  useEffect(() => {
    BoomUserStatusRef.current = BoomUserStatus;
  }, [BoomUserStatus]);

  useEffect(() => {
    BoomGlobalStateRef.current = BoomGlobalState;
  }, [BoomGlobalState]);

  useLayoutEffect(() => {
    // Si un utilisateur tente de craft l'URL on doit le rediriger vers la home
    if (
      !BoomGlobalStateRef.current ||
      Object.keys(BoomGlobalStateRef.current).length === 0 ||
      !isBoomEnabled ||
      (BoomUserStatusRef.current !== BoomUserStatuses.IN &&
        BoomGlobalStateRef.current.status === BoomGlobalStatuses.ENDING)
    ) {
      history.push(RoutesRedirectionFactory.getHomeRouteConfig());
    }

    // Si un utilisateur n'est pas connecté et qu'il n'est pas en train de se connecter
    // on doit le rediriger vers la page de login
    if (!isConnectedRef.current && !isConnectingRef.current) {
      if (Target.__MOBILE__) {
        history.push({
          ...RoutesRedirectionFactory.getLoginRouteConfig({
            state: {
              modal: true,
            },
          }),
        });
      } else {
        showWebsiteLoginForm();

        history.push(RoutesRedirectionFactory.getHomeRouteConfig());
      }
    }
  }, [history, isBoomEnabled]);

  useLayoutEffect(() => {
    // Si un utilisateur tente de craft l'URL on doit le rediriger vers la home
    if (!BoomGlobalStateRef.current || Object.keys(BoomGlobalStateRef.current).length === 0 || !isBoomEnabled) {
      history.push(RoutesRedirectionFactory.getHomeRouteConfig());
    }

    // Si un utilisateur n'est pas connecté et qu'il n'est pas en train de se connecter
    // on doit le rediriger vers la page de login
    if (!isConnectedRef.current && !isConnectingRef.current) {
      if (Target.__MOBILE__) {
        history.push({
          ...RoutesRedirectionFactory.getLoginRouteConfig({
            state: {
              modal: true,
            },
          }),
        });
      } else {
        showWebsiteLoginForm();

        history.push(RoutesRedirectionFactory.getHomeRouteConfig());
      }
    }
  }, [history, isBoomEnabled]);

  useLayoutEffect(() => {
    const routerSwitchWrapperElement = routerSwitchWrapperRef.current;

    if (routerSwitchWrapperElement && bannerElement) {
      const { height: bannerHeight } = bannerElement.getBoundingClientRect();

      if (typeof bannerHeight === 'number') {
        if (Target.__MOBILE__) {
          routerSwitchWrapperElement.style.paddingTop = `${bannerHeight}px`;
        } else {
          routerSwitchWrapperElement.style.marginTop = `${bannerHeight}px`;
        }
      }
    }
  }, [bannerElement]);

  useLayoutEffect(() => {
    const scrollElement = Target.__MOBILE__ ? getAppDOMElement() : window;

    const scrollCallback = () => {
      if (bannerElement) {
        bannerElement.style.top = `${scrollElement.scrollTop}px`;
      }
    };

    scrollElement.addEventListener('scroll', scrollCallback);

    return () => {
      scrollElement.removeEventListener('scroll', scrollCallback);
    };
  }, [bannerElement]);

  const modalFixedRender = useCallback(
    () => (
      <StyledInfosLabel
        ref={(ref) => setBannerElement(ref)}
        label={bannerLabel}
        type={isOperationEnding ? InfosLabelTypes.INFOS_YELLOW : InfosLabelTypes.INFOS_BLUE}
        symbol={'!'}
      />
    ),
    [bannerLabel, isOperationEnding],
  );

  const renderEndingBanner = useCallback(() => {
    if (showEndingBanner) {
      if (Target.__MOBILE__) {
        return modalFixedRender();
      }
      return <ModalFixedContent render={modalFixedRender} />;
    }

    return null;
  }, [modalFixedRender, showEndingBanner]);

  if (!isConnected && !isConnecting) {
    return <NotFoundView label={__('You must be logged in to see this page')} imgPath={NotConnected1} />;
  }

  if (didReceivedUserData) {
    return (
      <BoomContext.Provider value={boomContextValue}>
        <RouterSwitchWrapper ref={routerSwitchWrapperRef}>
          {renderEndingBanner()}
          <Switch>
            <Route path={RoutePatterns.BOOM_ENROLLMENT} exact>
              <BoomEnrollmentContainer />
            </Route>
            <BoomMainRouterSwitch className={className} />
          </Switch>
        </RouterSwitchWrapper>
      </BoomContext.Provider>
    );
  }

  return <StyledWinaLoader />;
};

const Wrapper = styled.div`
  --Modal-backButtonOpacity: 0.7;
  font-size: 0.95em;
`;

const fixedBannerStyle = css`
  pointer-events: none;
`;

type BoomRouterSwitchProps = {
  className?: string;
};

const BoomRouterSwitch = ({ className = '' }: BoomRouterSwitchProps) => {
  const { history } = useRouter();

  const [disableScrollView, setDisableScrollView] = useState(false);

  const modalContextValue = useMemo(
    () => ({
      disableScrollView,
      setDisableScrollView,
    }),
    [disableScrollView],
  );

  const onCloseModal = useCallback(() => {
    history.replace(RoutesRedirectionFactory.getHomeRouteConfig());
  }, [history]);

  return (
    <Wrapper className={className}>
      <Switch>
        <Route path={RoutePatterns.BOOM}>
          {!Target.__MOBILE__ ? (
            <BoomModalContext.Provider value={modalContextValue}>
              <Modal
                backgroundColor={'#000000'}
                contentSpacing={ModalContentSpacing.NONE}
                hasOverlay
                closeOnOutsideClick
                closeOnEsc
                onClose={onCloseModal}
                withBorder
                hasCloseButton
                disableScrollView={disableScrollView}
                forbidBackgroundScrolling
                fixedStyle={fixedBannerStyle}
              >
                <BoomSubRouterSwitch />
              </Modal>
            </BoomModalContext.Provider>
          ) : (
            <BoomSubRouterSwitch />
          )}
        </Route>
      </Switch>
    </Wrapper>
  );
};

export default BoomRouterSwitch;
